import type { ReactElement, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { featureFlagsEndpoints } from './endpoints';
import type { FeatureFlagMapDto } from './endpoints';
import { FEATURE_FLAGS_QUERY_KEY, FeatureFlagsContext } from './featureFlagsContext';

/**
 * Loads `GET /api/v1/features` and provides the per-user flag map to the app
 * (PTV2-1067 D26–D27).
 *
 * - **Render-blocking at bootstrap**: children don't mount while the first
 *   fetch is in flight, so there is no flash of wrong content. Mounted
 *   inside `SessionGuard` (the call needs an authenticated session) and
 *   above the router.
 * - **Refetch on window focus** (query option) and **on navigation**
 *   (`FeatureFlagsRouteSync` inside the router); no polling. Server-side
 *   propagation speed is the per-key `cacheTtl` (D16).
 * - **Fail-open**: if the fetch errors, the app renders with an EMPTY map —
 *   every flag reads as off/default. Flags are UX-only and never a security
 *   boundary, so blocking the whole app on this call would invert the risk.
 * - **`value` prop**: inject a static map for tests / Storybook — no query,
 *   no loading gate.
 */
export function FeatureFlagsProvider({
  children,
  value,
}: {
  readonly children: ReactNode;
  readonly value?: FeatureFlagMapDto;
}): ReactElement {
  if (value !== undefined) {
    return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
  }
  return <QueryBackedProvider>{children}</QueryBackedProvider>;
}

function QueryBackedProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: ({ signal }) => featureFlagsEndpoints.getFeatureFlags({ signal }),
    // Always refetch when asked (navigation / focus) — kill-switch latency is
    // bounded by the server-side per-key cacheTtl, not by client staleness.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  if (query.isPending) {
    return (
      <main
        role="status"
        aria-live="polite"
        className={cn(
          'mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-6 p-8',
          'text-foreground',
        )}
      >
        <p className="text-sm text-muted-foreground">{t('app.loading')}</p>
      </main>
    );
  }

  // Error → empty map (fail-open, all flags off/default). On a refetch error
  // TanStack keeps the last good data, so a transient failure mid-session
  // never flips flags off.
  const flags = query.data ?? {};
  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>;
}
